// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import TextField from "@insite/mobius/TextField";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import sendExportRegistrants from "@mcmc/App/Handlers/ExportRegistrants/SendExportRegistrants";
import EllipsisSpinner from "@mcmc/XDL/Components/Common/EllipsisSpinner";
import * as React from "react";
import { ChangeEvent, ReactNode, useContext, useState } from "react";
import { connect, ResolveThunks } from "react-redux";

interface OwnProps {}

const mapDispatchToProps = {
    sendExportRegistrants,
};

type Props = OwnProps & ResolveThunks<typeof mapDispatchToProps>;

const StyledForm = getStyledWrapper("form");

export interface ExportRegistrantsPageStyles {
    container?: GridContainerProps;
    courseNoGridItem?: GridItemProps;
    descriptionGridItem?: GridItemProps;
    buttonGridItem?: GridItemProps;
    description?: TypographyProps;
    exportButton?: ButtonPresentationProps;
}

export const exportRegistrantsPageStyles: ExportRegistrantsPageStyles = {
    container: {
        gap: 30,
    },
    courseNoGridItem: {
        width: 12,
    },
    descriptionGridItem: {
        width: 12,
    },
    buttonGridItem: {
        width: 12,
    },
};
const styles = exportRegistrantsPageStyles;

const ExportRegistrants = ({ sendExportRegistrants }: Props) => {
    const [isSendingEmailDone, setIsSendingEmailDone] = useState(true);
    const toasterContext = useContext(ToasterContext);
    const [courseNumber, setCourseNumber] = useState("");
    const [courseNumberError, setCourseNumberError] = useState<ReactNode>("");

    const submitHandler = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        event.stopPropagation();

        if (validateCourseNumber(courseNumber)) {
            return;
        }
        setIsSendingEmailDone(false);
        sendExportRegistrants({
            courseNumber,
            onSuccess: () => {
                setIsSendingEmailDone(true);
                toasterContext.addToast({
                    body: siteMessage("McMc_sendExportRegistrants_Success_Message"),
                    messageType: "success",
                });
            },
            onError: error => {
                setIsSendingEmailDone(true);
                toasterContext.addToast({
                    body: `${error}`,
                    messageType: "danger",
                });
            },
        });
    };

    const courseNoChangeHandler = (event: ChangeEvent<HTMLInputElement>) => {
        const courseNo = event.currentTarget.value;
        validateCourseNumber(courseNo);
        setCourseNumber(courseNo);
    };

    const validateCourseNumber = (courseNumber: string) => {
        const courseNumberValid = courseNumber.trim();
        const courseNumberError = courseNumberValid ? "" : siteMessage("McMc_Field_Required");
        setCourseNumberError(courseNumberError);
        return !!courseNumberError;
    };

    return (
        <StyledForm onSubmit={submitHandler} noValidate>
            <GridContainer {...styles.container} data-test-selector="productSelector">
                <GridItem {...styles.descriptionGridItem}>
                    <Typography {...styles.description}>{siteMessage("McMc_ExportRegistrants_description")}</Typography>
                </GridItem>
                <GridItem {...styles.courseNoGridItem}>
                    <TextField
                        type="text"
                        label={translate("Course Number")}
                        data-test-selector="course_number"
                        required
                        error={courseNumberError}
                        value={courseNumber}
                        onChange={courseNoChangeHandler}
                    />
                </GridItem>
                <GridItem {...styles.buttonGridItem}>
                    <Button {...styles.exportButton} disabled={!isSendingEmailDone} type="submit">
                        <EllipsisSpinner label={translate("Export Registrants")} showSpinner={!isSendingEmailDone} />
                    </Button>
                </GridItem>
            </GridContainer>
        </StyledForm>
    );
};

const widgetModule: WidgetModule = {
    component: connect(null, mapDispatchToProps)(ExportRegistrants),
    definition: {
        group: "Product List",
        allowedContexts: ["ExportRegistrantsPage" as any],
    },
};

export default widgetModule;
